import { useLocation, Link } from 'react-router-dom';
import {
  ListItem as MuiListItem,
  ListItemIcon as MuiListItemIcon,
  ListItemText as MuiListItemText,
} from '@mui/material';
import { ChevronRight as ChevronRightIcon } from '@mui/icons-material';

import ProtectedComponentBase from '../../../components/main/protected/ProtectedComponent';
import { withShowable } from '../lib/withShowable';
import {
  useIconStyles,
  useItemStyles,
  useItemTextStyles,
  useSecondaryNavIcon,
} from './styles';
import { SidebarSecondaryNav } from './SidebarSecondaryNav';
import { RailTooltip } from './RailTooltip';

const ProtectedComponent = withShowable(ProtectedComponentBase);

interface RenderMuiListItemProps {
  readonly route: any;
  readonly to: string;
  readonly finalURL: string;
  readonly handleClick: () => void;
  /** The sidebar is the icon-only rail, so the name shows on hover instead. */
  readonly railCollapsed?: boolean;
}

export function RenderMuiListItem({
  route,
  to,
  finalURL,
  handleClick,
  railCollapsed = false,
}: RenderMuiListItemProps) {
  const itemClasses = useItemStyles();
  const itemTextClasses = useItemTextStyles();
  const itemIconClasses = useIconStyles();
  const secondaryIconClasses = useSecondaryNavIcon();
  const location = useLocation();

  if (!route.subroutes) {
    return (
      // `allowEither`/`allowIfAll` are forwarded here as well as on the
      // submenu branch below. A leaf route that qualifies either way -- the
      // policyholder's Users page, which opens for users:view OR roles:view --
      // otherwise reached ProtectedComponent with an undefined `allow` and was
      // dropped from the sidebar despite its page being reachable by URL.
      <ProtectedComponent
        allow={route.allow}
        allowEither={route.allowEither}
        allowIfAll={route.allowIfAll}
        key={to}
      >
        {/* A disabled row takes no pointer events, so a tooltip on it could
          never open. */}
        <RailTooltip
          railCollapsed={railCollapsed && !route.nav.disabled}
          title={route.nav.name}
        >
          <MuiListItem
            button={true as any}
            classes={itemClasses}
            selected={to.startsWith(finalURL)}
            component={Link as any}
            to={to.split('/:').shift()}
            onClick={handleClick}
            data-qa={`SIDEBAR_NAVLINK:${route.nav.name}`}
            disabled={route.nav.disabled ?? false}
          >
            {typeof route.nav.Component === 'function' ? (
              <route.nav.Component
                icon={route.nav.icon}
                itemTextClasses={itemTextClasses}
                name={route.nav.name}
                itemIconClasses={itemIconClasses}
              />
            ) : (
              <>
                <MuiListItemIcon
                  classes={itemIconClasses}
                  data-testid="route-nav-icon"
                >
                  {route.nav.icon}
                </MuiListItemIcon>
                <MuiListItemText
                  classes={itemTextClasses}
                  disableTypography
                  secondary={route.nav.name}
                  data-testid="route-nav-name"
                />
              </>
            )}
          </MuiListItem>
        </RailTooltip>
      </ProtectedComponent>
    );
  }

  return (
    <ProtectedComponent
      allow={route.allow}
      allowEither={route.allowEither}
      allowIfAll={route.allowIfAll}
      key={to}
    >
      <SidebarSecondaryNav
        routes={route.subroutes}
        parent={route.nav.name}
        railCollapsed={railCollapsed}
      >
        <MuiListItem
          data-submenu
          button
          classes={itemClasses}
          selected={location.pathname.indexOf(to) !== -1}
          component="a"
          data-qa={`SIDEBAR_NAVLINK:${route.nav.name}`}
        >
          <MuiListItemIcon
            classes={itemIconClasses}
            data-testid="route-nav-icon"
          >
            {route.nav.icon}
          </MuiListItemIcon>

          <MuiListItemText
            classes={itemTextClasses}
            disableTypography
            secondary={route.nav.name}
            data-testid="route-nav-name"
          />

          <ChevronRightIcon classes={secondaryIconClasses} />
        </MuiListItem>
      </SidebarSecondaryNav>
    </ProtectedComponent>
  );
}
